import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  TextField,
  MenuItem,
  Select,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { format } from 'date-fns';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import rechnungenService from '../services/rechnungenService';
import { useSearchParamsState } from '../hooks/useSearchParamsState';
import { usePermissions } from '../hooks/usePermissions';
import { useNotification } from '../hooks/NotificationContext';
import * as P from '../constants/permissions';
import { RechnungList, ZeiteintragAbweichung } from '../types';

type SortField = 'rechnungsnummer' | 'rechnungsdatum' | 'projekt' | 'kunde' | 'status' | 'nettobetrag';

interface Filters {
  rechnungsnummer: string;
  rechnungsdatum: string;
  projekt: string;
  kunde: string;
  status: string;
}

const STATUS_PRIO: Record<string, number> = {
  Entwurf: 1,
  Versendet: 2,
  Bezahlt: 3,
  Storniert: 4,
};

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'Entwurf': return 'default';
    case 'Versendet': return 'primary';
    case 'Bezahlt': return 'success';
    case 'Storniert': return 'error';
    default: return 'default';
  }
};

const RechnungenListPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { showSuccess } = useNotification();

  const [rechnungen, setRechnungen] = useState<RechnungList[]>([]);
  const [loading, setLoading] = useState(true);
  const [abweichungen, setAbweichungen] = useState<ZeiteintragAbweichung[]>([]);
  const [syncingAbweichungen, setSyncingAbweichungen] = useState(false);

  const [params, setParams, resetParams, hasActiveFilters] = useSearchParamsState({
    defaults: {
      nr: '',
      datum: '',
      projekt: '',
      kunde: '',
      status: '',
      sort: '',
      abgeschlossen: false as boolean,
    },
  });

  const filters: Filters = {
    rechnungsnummer: params.nr as string,
    rechnungsdatum: params.datum as string,
    projekt: params.projekt as string,
    kunde: params.kunde as string,
    status: params.status as string,
  };
  const showAbgeschlossen = params.abgeschlossen as boolean;

  const sortParts = (params.sort as string).split(':');
  const sortField: SortField | null = sortParts[0] ? (sortParts[0] as SortField) : null;
  const sortDirection: 'asc' | 'desc' = sortParts[1] === 'desc' ? 'desc' : 'asc';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setRechnungen(await rechnungenService.list());
        try {
          setAbweichungen(await rechnungenService.listZeiteintragAbweichungen());
        } catch {
          setAbweichungen([]);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Rechnungen:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setParams({ sort: `${field}:desc` });
      } else {
        setParams({ sort: '' });
      }
    } else {
      setParams({ sort: `${field}:asc` });
    }
  };

  const FILTER_KEY_MAP: Record<keyof Filters, string> = {
    rechnungsnummer: 'nr',
    rechnungsdatum: 'datum',
    projekt: 'projekt',
    kunde: 'kunde',
    status: 'status',
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setParams({ [FILTER_KEY_MAP[field]]: value } as any);
  };

  const displayedRechnungen = useMemo(() => {
    let result = [...rechnungen];

    if (!showAbgeschlossen) {
      result = result.filter((r) => r.status !== 'Bezahlt' && r.status !== 'Storniert');
    }

    if (filters.rechnungsnummer) {
      const q = filters.rechnungsnummer.toLowerCase();
      result = result.filter((r) => r.rechnungsnummer.toLowerCase().includes(q));
    }
    if (filters.rechnungsdatum) {
      const q = filters.rechnungsdatum.toLowerCase();
      result = result.filter((r) => format(new Date(r.rechnungsdatum), 'dd.MM.yyyy').includes(q));
    }
    if (filters.projekt) {
      const q = filters.projekt.toLowerCase();
      result = result.filter((r) => (r.projekt_name || '').toLowerCase().includes(q));
    }
    if (filters.kunde) {
      const q = filters.kunde.toLowerCase();
      result = result.filter((r) =>
        (r.empfaenger_name || '').toLowerCase().includes(q)
        || (r.kunde_name || '').toLowerCase().includes(q)
      );
    }
    if (filters.status) {
      result = result.filter((r) => r.status === filters.status);
    }

    if (sortField) {
      result.sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
          case 'rechnungsnummer':
            cmp = a.rechnungsnummer.localeCompare(b.rechnungsnummer, 'de');
            break;
          case 'rechnungsdatum':
            cmp = new Date(a.rechnungsdatum).getTime() - new Date(b.rechnungsdatum).getTime();
            break;
          case 'projekt':
            cmp = (a.projekt_name || '').localeCompare(b.projekt_name || '', 'de');
            break;
          case 'kunde':
            cmp = (a.empfaenger_name || a.kunde_name || '').localeCompare(b.empfaenger_name || b.kunde_name || '', 'de');
            break;
          case 'status':
            cmp = (STATUS_PRIO[a.status] ?? 99) - (STATUS_PRIO[b.status] ?? 99);
            break;
          case 'nettobetrag':
            cmp = a.nettobetrag - b.nettobetrag;
            break;
        }
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    } else {
      result.sort((a, b) => {
        const prioA = STATUS_PRIO[a.status] ?? 99;
        const prioB = STATUS_PRIO[b.status] ?? 99;
        if (prioA !== prioB) return prioA - prioB;
        return new Date(b.rechnungsdatum).getTime() - new Date(a.rechnungsdatum).getTime();
      });
    }

    return result;
  }, [rechnungen, filters, sortField, sortDirection, showAbgeschlossen]);

  const sumNetto = displayedRechnungen.reduce((s, r) => s + r.nettobetrag, 0);
  const offenNetto = displayedRechnungen.filter((r) => r.status === 'Versendet').reduce((s, r) => s + r.nettobetrag, 0);
  const bezahltNetto = displayedRechnungen.filter((r) => r.status === 'Bezahlt').reduce((s, r) => s + r.nettobetrag, 0);

  if (loading) {
    return <TableSkeleton columns={7} rows={8} />;
  }

  const renderSortLabel = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const filterInputSx = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8125rem' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.divider, 0.3) },
  };

  return (
    <Box>
      <PageHeader
        title="Rechnungen"
        breadcrumbs={[{ label: 'Rechnungen' }]}
        onAdd={() => navigate('/rechnungen/neu')}
        addLabel="Neue Rechnung"
        actions={hasPermission(P.RECHNUNGEN_SETTINGS) ? (
          <Button variant="outlined" onClick={() => navigate('/rechnungen/textvorlagen')}>
            Vorlagen
          </Button>
        ) : undefined}
      />

      {abweichungen.length > 0 && hasPermission(P.RECHNUNGEN_EDIT) && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={syncingAbweichungen}
              onClick={async () => {
                try {
                  setSyncingAbweichungen(true);
                  const result = await rechnungenService.syncAlleZeiteintragAbweichungen();
                  setAbweichungen(await rechnungenService.listZeiteintragAbweichungen());
                  setRechnungen(await rechnungenService.list());
                  showSuccess(
                    result.anzahl_geloest === 0
                      ? 'Keine Korrektur notwendig.'
                      : `${result.anzahl_geloest} Zeiteinträge (${result.stunden_geloest.toFixed(1)}h) auf ${result.anzahl_rechnungen} Rechnungen wieder auf nicht abgerechnet gesetzt.`,
                  );
                } catch {
                  /* interceptor */
                } finally {
                  setSyncingAbweichungen(false);
                }
              }}
            >
              Alle korrigieren
            </Button>
          }
        >
          {abweichungen.length === 1
            ? `Rechnung ${abweichungen[0].rechnungsnummer} hat ${abweichungen[0].anzahl_ausserhalb} Zeiteinträge außerhalb des Leistungszeitraums.`
            : `${abweichungen.length} Rechnungen haben Zeiteinträge außerhalb ihres Leistungszeitraums.`}
          {' '}Die Rechnungsbeträge bleiben unverändert.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, px: { xs: 1.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Gesamt (Netto)</Typography>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                {sumNetto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, px: { xs: 1.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Offen</Typography>
              <Typography variant="h5" color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                {offenNetto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, px: { xs: 1.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Bezahlt</Typography>
              <Typography variant="h5" color="success.main" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                {bezahltNetto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, px: { xs: 1.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Anzahl</Typography>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>{displayedRechnungen.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Toggle + Reset */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, ml: 0.5 }}>
        <FormControlLabel
          control={<Switch checked={showAbgeschlossen} onChange={(e) => setParams({ abgeschlossen: e.target.checked })} size="small" />}
          label="Bezahlte/Stornierte anzeigen"
          slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
        />
        {hasActiveFilters && (
          <Button size="small" startIcon={<FilterListOffIcon />} onClick={resetParams} sx={{ ml: 2 }}>
            Filter zurücksetzen
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>{renderSortLabel('rechnungsnummer', 'Rechnungsnr.')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{renderSortLabel('rechnungsdatum', 'Datum')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{renderSortLabel('projekt', 'Projekt')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{renderSortLabel('kunde', 'Empfänger')}</TableCell>
              <TableCell>{renderSortLabel('status', 'Status')}</TableCell>
              <TableCell align="right">{renderSortLabel('nettobetrag', 'Netto')}</TableCell>
              <TableCell align="right">PDF</TableCell>
            </TableRow>

            {/* Filter row */}
            <TableRow>
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <TextField size="small" fullWidth placeholder="Filter..." value={filters.rechnungsnummer}
                  onChange={(e) => handleFilterChange('rechnungsnummer', e.target.value)} sx={filterInputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }} />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                <TextField size="small" fullWidth placeholder="Filter..." value={filters.rechnungsdatum}
                  onChange={(e) => handleFilterChange('rechnungsdatum', e.target.value)} sx={filterInputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }} />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', md: 'table-cell' } }}>
                <TextField size="small" fullWidth placeholder="Filter..." value={filters.projekt}
                  onChange={(e) => handleFilterChange('projekt', e.target.value)} sx={filterInputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }} />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', md: 'table-cell' } }}>
                <TextField size="small" fullWidth placeholder="Filter..." value={filters.kunde}
                  onChange={(e) => handleFilterChange('kunde', e.target.value)} sx={filterInputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }} />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <Select size="small" fullWidth displayEmpty value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{ ...filterInputSx, '& .MuiSelect-select': { py: 0.75, fontSize: '0.8125rem' } }}>
                  <MenuItem value="">Alle</MenuItem>
                  <MenuItem value="Entwurf">Entwurf</MenuItem>
                  <MenuItem value="Versendet">Versendet</MenuItem>
                  <MenuItem value="Bezahlt">Bezahlt</MenuItem>
                  <MenuItem value="Storniert">Storniert</MenuItem>
                </Select>
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1 }} />
              <TableCell sx={{ pt: 0.5, pb: 1 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRechnungen.map((re) => (
              <TableRow key={re.id} hover sx={{ cursor: 'pointer' }} {...navigableProps(navigate, `/rechnungen/${re.id}`)}>
                <TableCell sx={{ fontWeight: 500 }}>{re.rechnungsnummer}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{format(new Date(re.rechnungsdatum), 'dd.MM.yyyy')}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{re.projekt_name || '-'}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {re.empfaenger_name || re.kunde_name || '-'}
                  {re.aufteilung_bezeichnung && (
                    <Chip
                      label={`${re.aufteilung_bezeichnung}${re.aufteilung_prozent != null ? ` ${re.aufteilung_prozent}%` : ''}`}
                      size="small"
                      sx={{ ml: 0.75, height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                </TableCell>
                <TableCell><Chip label={re.status} size="small" color={getStatusColor(re.status)} /></TableCell>
                <TableCell align="right">{re.nettobetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                <TableCell align="right">
                  <Tooltip title="PDF herunterladen">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); rechnungenService.generatePdf(re.id); }}>
                      <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {displayedRechnungen.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Rechnungen gefunden.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RechnungenListPage;
